import {
  homepageSectionTemplateKinds,
  type HomepageAddedSection,
  type HomepageContent,
  type HomepageSectionTemplateKind,
} from "@whitehaven/contracts/homepage";
import {
  HomepageContentView,
  updateHomepagePlainText,
} from "@whitehaven/homepage-view";
import { countHomepageTextCharacters } from "@whitehaven/validation/homepage";
import { useState, type ChangeEvent } from "react";
import { homepageEditorSectionDefinitions } from "./homepage-editor-model";

interface EditorField {
  readonly path: string;
  readonly label: string;
  readonly multiline?: boolean;
  readonly maximum?: number;
}

const fields = {
  seo: [
    { path: "seo.title", label: "Search title", maximum: 70 },
    {
      path: "seo.description",
      label: "Search description",
      multiline: true,
      maximum: 180,
    },
  ],
  theme: [],
  hero: [
    { path: "hero.eyebrow", label: "Location label" },
    { path: "hero.headingLead", label: "Headline opening" },
    { path: "hero.headingAccent", label: "Headline accent" },
    { path: "hero.lead", label: "Introduction", multiline: true },
    { path: "hero.primaryActionLabel", label: "Primary button" },
    { path: "hero.secondaryActionLabel", label: "Secondary button" },
    ...[0, 1, 2].map((index) => ({
      path: `hero.trustItems.${index}`,
      label: `Trust message ${index + 1}`,
    })),
    { path: "hero.visitCard.title", label: "Visit card title" },
    { path: "hero.visitCard.status", label: "Visit card status" },
    ...[0, 1, 2].flatMap((index) => [
      {
        path: `hero.visitCard.steps.${index}.title`,
        label: `Visit step ${index + 1} title`,
      },
      {
        path: `hero.visitCard.steps.${index}.body`,
        label: `Visit step ${index + 1} detail`,
      },
    ]),
    ...[0, 1, 2, 3].map((index) => ({
      path: `hero.trustStrip.${index}`,
      label: `Trust strip item ${index + 1}`,
    })),
  ],
  services: [
    { path: "services.eyebrow", label: "Section label" },
    { path: "services.heading", label: "Heading", multiline: true },
    { path: "services.body", label: "Introduction", multiline: true },
  ],
  bookingSteps: [
    { path: "bookingSteps.eyebrow", label: "Section label" },
    { path: "bookingSteps.heading", label: "Heading", multiline: true },
    { path: "bookingSteps.body", label: "Introduction", multiline: true },
    ...[0, 1, 2].flatMap((index) => [
      {
        path: `bookingSteps.steps.${index}.title`,
        label: `Step ${index + 1} title`,
      },
      {
        path: `bookingSteps.steps.${index}.body`,
        label: `Step ${index + 1} explanation`,
        multiline: true,
      },
    ]),
  ],
  packages: [
    { path: "packages.eyebrow", label: "Section label" },
    { path: "packages.heading", label: "Heading", multiline: true },
    { path: "packages.body", label: "Introduction", multiline: true },
  ],
  careBoundary: [
    { path: "careBoundary.eyebrow", label: "Section label" },
    { path: "careBoundary.heading", label: "Heading", multiline: true },
    { path: "careBoundary.body", label: "Introduction", multiline: true },
    ...[0, 1].flatMap((card) => [
      {
        path: `careBoundary.cards.${card}.label`,
        label: `Column ${card + 1} label`,
      },
      {
        path: `careBoundary.cards.${card}.title`,
        label: `Column ${card + 1} heading`,
      },
      {
        path: `careBoundary.cards.${card}.body`,
        label: `Column ${card + 1} explanation`,
        multiline: true,
      },
      ...[0, 1, 2].map((bullet) => ({
        path: `careBoundary.cards.${card}.bullets.${bullet}`,
        label: `Column ${card + 1} point ${bullet + 1}`,
      })),
    ]),
  ],
  guides: [
    { path: "guides.eyebrow", label: "Section label" },
    { path: "guides.heading", label: "Heading", multiline: true },
    { path: "guides.body", label: "Introduction", multiline: true },
    ...[0, 1, 2].flatMap((card) => [
      {
        path: `guides.cards.${card}.eyebrow`,
        label: `Guide ${card + 1} label`,
      },
      { path: `guides.cards.${card}.title`, label: `Guide ${card + 1} title` },
      {
        path: `guides.cards.${card}.body`,
        label: `Guide ${card + 1} summary`,
        multiline: true,
      },
      {
        path: `guides.cards.${card}.meta`,
        label: `Guide ${card + 1} metadata`,
      },
      {
        path: `guides.cards.${card}.actionLabel`,
        label: `Guide ${card + 1} action`,
      },
    ]),
  ],
  faq: [
    { path: "faq.eyebrow", label: "Section label" },
    { path: "faq.heading", label: "Heading", multiline: true },
    { path: "faq.body", label: "Introduction", multiline: true },
    { path: "faq.actionLabel", label: "Section action" },
    ...[0, 1, 2, 3, 4].flatMap((index) => [
      { path: `faq.items.${index}.question`, label: `Question ${index + 1}` },
      {
        path: `faq.items.${index}.answer`,
        label: `Answer ${index + 1}`,
        multiline: true,
      },
    ]),
  ],
  contact: [
    { path: "contact.eyebrow", label: "Section label" },
    { path: "contact.heading", label: "Heading", multiline: true },
    { path: "contact.body", label: "Introduction", multiline: true },
    { path: "contact.email", label: "Public email" },
    { path: "contact.privacyNotice", label: "Privacy notice", multiline: true },
    {
      path: "contact.availabilityNotice",
      label: "Availability notice",
      multiline: true,
    },
    { path: "contact.formHelp", label: "Form help" },
    {
      path: "contact.consentLabel",
      label: "Consent statement",
      multiline: true,
    },
    { path: "contact.actionLabel", label: "Form button" },
  ],
  addedSections: [],
  footer: [
    {
      path: "footer.description",
      label: "Footer description",
      multiline: true,
    },
    { path: "footer.legalLine", label: "Wellness notice" },
  ],
} as const satisfies Readonly<
  Record<
    (typeof homepageEditorSectionDefinitions)[number]["id"],
    readonly EditorField[]
  >
>;

const templateLabels: Readonly<Record<HomepageSectionTemplateKind, string>> = {
  hero: "Hero / feature",
  cards: "Cards / grid",
  richText: "Rich text",
  callToAction: "Call to action",
  faq: "FAQ",
};

export interface HomepageVisualEditorProps {
  readonly draft: HomepageContent;
  readonly disabled?: boolean;
  readonly canCreateSections?: boolean;
  readonly canManageSections?: boolean;
  readonly canManageTheme?: boolean;
  readonly onChange: (content: HomepageContent) => void;
}

function valueAtPath(content: HomepageContent, path: string): string {
  let current: unknown = content;
  for (const segment of path.split(".")) {
    if (current === null || typeof current !== "object") return "";
    current = Array.isArray(current)
      ? current[Number(segment)]
      : (current as Readonly<Record<string, unknown>>)[segment];
  }
  return typeof current === "string" ? current : "";
}

function EditableField({
  content,
  field,
  disabled,
  onChange,
}: {
  readonly content: HomepageContent;
  readonly field: EditorField;
  readonly disabled: boolean;
  readonly onChange: (content: HomepageContent) => void;
}) {
  const value = valueAtPath(content, field.path);
  const maximum = field.maximum ?? (field.multiline ? 900 : 254);
  const count = countHomepageTextCharacters(value);
  const shared = {
    id: `homepage-${field.path.replace(/\./gu, "-")}`,
    value,
    disabled,
    required: true,
    "data-homepage-path": field.path,
    "aria-invalid": count > maximum || undefined,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(
        updateHomepagePlainText(content, field.path, event.currentTarget.value),
      ),
  } as const;

  return (
    <label className="homepage-section-editor__field" htmlFor={shared.id}>
      <span>{field.label}</span>
      {field.multiline ? (
        <textarea {...shared} rows={3} />
      ) : (
        <input
          {...shared}
          type={field.path === "contact.email" ? "email" : "text"}
        />
      )}
      <small>
        {count}/{maximum} characters
      </small>
    </label>
  );
}

function createSection(
  kind: HomepageSectionTemplateKind,
): HomepageAddedSection {
  const base = {
    id: `section-${crypto.randomUUID()}`,
    eyebrow: "New section",
    heading: "Replace with an approved heading",
    body: "Complete this section before saving the draft revision.",
    presentation: {
      layout: "auto",
      alignment: "left",
      spacing: "standard",
      accent: "none",
    },
  } as const;
  if (kind === "hero" || kind === "callToAction") {
    return { ...base, kind, actionLabel: "Learn more", actionHref: "#contact" };
  }
  if (kind === "cards") {
    return {
      ...base,
      kind,
      cards: [
        {
          id: `card-${crypto.randomUUID()}`,
          title: "Card one",
          body: "Replace with approved content.",
        },
        {
          id: `card-${crypto.randomUUID()}`,
          title: "Card two",
          body: "Replace with approved content.",
        },
        {
          id: `card-${crypto.randomUUID()}`,
          title: "Card three",
          body: "Replace with approved content.",
        },
      ],
    };
  }
  if (kind === "faq") {
    return {
      ...base,
      kind,
      items: [
        {
          question: "Add a common question?",
          answer: "Replace with an approved answer.",
        },
        {
          question: "Add another question?",
          answer: "Replace with an approved answer.",
        },
      ],
    };
  }
  return {
    ...base,
    kind: "richText",
    paragraphs: [
      "Replace this paragraph with approved visitor-facing content.",
    ],
  };
}

function ThemeControls({
  content,
  disabled,
  onChange,
}: {
  readonly content: HomepageContent;
  readonly disabled: boolean;
  readonly onChange: (content: HomepageContent) => void;
}) {
  const controls = [
    {
      key: "palette",
      label: "Palette",
      options: ["whitehaven", "ocean", "island"],
    },
    {
      key: "typography",
      label: "Typography",
      options: ["serif-accent", "modern-sans"],
    },
    {
      key: "spacing",
      label: "Spacing",
      options: ["balanced", "compact", "airy"],
    },
    { key: "buttons", label: "Buttons", options: ["rounded", "pill"] },
    { key: "cards", label: "Cards", options: ["soft", "outlined"] },
  ] as const;
  return (
    <div className="homepage-section-editor__theme-grid">
      {controls.map((control) => (
        <label key={control.key}>
          <span>{control.label}</span>
          <select
            disabled={disabled}
            value={content.theme[control.key]}
            onChange={(event) =>
              onChange({
                ...content,
                theme: {
                  ...content.theme,
                  [control.key]: event.currentTarget.value,
                },
              } as HomepageContent)
            }
          >
            {control.options.map((option) => (
              <option key={option} value={option}>
                {option.replace(/-/gu, " ")}
              </option>
            ))}
          </select>
        </label>
      ))}
      <p className="homepage-section-editor__catalog-note">
        Only accessibility-reviewed tokens are available. Custom CSS, HTML, and
        scripts are never stored in homepage content.
      </p>
    </div>
  );
}

function AddedSectionsEditor({
  content,
  disabled,
  canCreate,
  canManage,
  onChange,
}: {
  readonly content: HomepageContent;
  readonly disabled: boolean;
  readonly canCreate: boolean;
  readonly canManage: boolean;
  readonly onChange: (content: HomepageContent) => void;
}) {
  const setSections = (addedSections: readonly HomepageAddedSection[]) =>
    onChange({ ...content, addedSections });
  return (
    <div className="homepage-section-editor__library">
      <div
        className="homepage-section-editor__template-list"
        aria-label="Section templates"
      >
        {homepageSectionTemplateKinds.map((kind) => (
          <button
            type="button"
            data-section-template={kind}
            key={kind}
            disabled={!canCreate || content.addedSections.length >= 12}
            onClick={() =>
              setSections([...content.addedSections, createSection(kind)])
            }
          >
            Add {templateLabels[kind]}
          </button>
        ))}
      </div>
      {!canCreate && (
        <p className="homepage-section-editor__catalog-note">
          Your role can view this library but cannot add sections.
        </p>
      )}
      {content.addedSections.map((section, sectionIndex) => {
        const prefix = `addedSections.${sectionIndex}`;
        const sectionFields: EditorField[] = [
          { path: `${prefix}.eyebrow`, label: "Section label" },
          { path: `${prefix}.heading`, label: "Heading" },
          { path: `${prefix}.body`, label: "Introduction", multiline: true },
        ];
        if (section.kind === "hero" || section.kind === "callToAction") {
          sectionFields.push(
            { path: `${prefix}.actionLabel`, label: "Button label" },
            { path: `${prefix}.actionHref`, label: "Internal path or anchor" },
          );
        } else if (section.kind === "cards") {
          section.cards.forEach((_card, index) => {
            sectionFields.push(
              {
                path: `${prefix}.cards.${index}.title`,
                label: `Card ${index + 1} title`,
              },
              {
                path: `${prefix}.cards.${index}.body`,
                label: `Card ${index + 1} body`,
                multiline: true,
              },
            );
          });
        } else if (section.kind === "faq") {
          section.items.forEach((_item, index) => {
            sectionFields.push(
              {
                path: `${prefix}.items.${index}.question`,
                label: `Question ${index + 1}`,
              },
              {
                path: `${prefix}.items.${index}.answer`,
                label: `Answer ${index + 1}`,
                multiline: true,
              },
            );
          });
        } else {
          section.paragraphs.forEach((_paragraph, index) => {
            sectionFields.push({
              path: `${prefix}.paragraphs.${index}`,
              label: `Paragraph ${index + 1}`,
              multiline: true,
            });
          });
        }
        return (
          <article
            className="homepage-section-editor__added-section"
            key={section.id}
          >
            <header>
              <div>
                <span>{templateLabels[section.kind]}</span>
                <b>{section.heading}</b>
              </div>
              <div className="homepage-section-editor__added-actions">
                <button
                  type="button"
                  disabled={!canManage || sectionIndex === 0}
                  onClick={() => {
                    const next = [...content.addedSections];
                    const selected = next[sectionIndex];
                    if (!selected) return;
                    next.splice(sectionIndex, 1);
                    next.splice(sectionIndex - 1, 0, selected);
                    setSections(next);
                  }}
                >
                  Move up
                </button>
                <button
                  type="button"
                  disabled={
                    !canManage ||
                    sectionIndex === content.addedSections.length - 1
                  }
                  onClick={() => {
                    const next = [...content.addedSections];
                    const selected = next[sectionIndex];
                    if (!selected) return;
                    next.splice(sectionIndex, 1);
                    next.splice(sectionIndex + 1, 0, selected);
                    setSections(next);
                  }}
                >
                  Move down
                </button>
                <button
                  type="button"
                  disabled={!canManage}
                  onClick={() =>
                    setSections(
                      content.addedSections.filter(
                        (item) => item.id !== section.id,
                      ),
                    )
                  }
                >
                  Remove
                </button>
              </div>
            </header>
            <div className="homepage-section-editor__layout-controls">
              {(
                [
                  [
                    "layout",
                    "Responsive layout",
                    ["auto", "two-column", "three-column"],
                  ],
                  ["alignment", "Content alignment", ["left", "center"]],
                  [
                    "spacing",
                    "Section spacing",
                    ["standard", "compact", "airy"],
                  ],
                  [
                    "accent",
                    "Approved accent",
                    ["none", "leaf", "sun", "wave"],
                  ],
                ] as const
              ).map(([key, label, options]) => (
                <label key={key}>
                  <span>{label}</span>
                  <select
                    disabled={!canManage}
                    value={section.presentation[key]}
                    onChange={(event) =>
                      onChange(
                        updateHomepagePlainText(
                          content,
                          `${prefix}.presentation.${key}`,
                          event.currentTarget.value,
                        ),
                      )
                    }
                  >
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option.replace(/-/gu, " ")}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <div className="homepage-section-editor__fields">
              {sectionFields.map((field) => (
                <EditableField
                  content={content}
                  disabled={disabled}
                  field={field}
                  key={field.path}
                  onChange={onChange}
                />
              ))}
            </div>
            {section.kind === "cards" ? (
              <div className="homepage-section-editor__card-controls">
                <b>Card order ({section.cards.length}/6)</b>
                <ol>
                  {section.cards.map((card, cardIndex) => (
                    <li key={card.id}>
                      <span>{card.title}</span>
                      <button
                        type="button"
                        disabled={!canManage || cardIndex === 0}
                        onClick={() => {
                          const cards = [...section.cards];
                          const selected = cards[cardIndex];
                          if (!selected) return;
                          cards.splice(cardIndex, 1);
                          cards.splice(cardIndex - 1, 0, selected);
                          const sections = [...content.addedSections];
                          sections[sectionIndex] = { ...section, cards };
                          setSections(sections);
                        }}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        disabled={
                          !canManage || cardIndex === section.cards.length - 1
                        }
                        onClick={() => {
                          const cards = [...section.cards];
                          const selected = cards[cardIndex];
                          if (!selected) return;
                          cards.splice(cardIndex, 1);
                          cards.splice(cardIndex + 1, 0, selected);
                          const sections = [...content.addedSections];
                          sections[sectionIndex] = { ...section, cards };
                          setSections(sections);
                        }}
                      >
                        Down
                      </button>
                      <button
                        type="button"
                        disabled={!canManage || section.cards.length <= 2}
                        onClick={() => {
                          const cards = section.cards.filter(
                            (_item, index) => index !== cardIndex,
                          );
                          const sections = [...content.addedSections];
                          sections[sectionIndex] = { ...section, cards };
                          setSections(sections);
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ol>
                <button
                  type="button"
                  disabled={!canCreate || section.cards.length >= 6}
                  onClick={() => {
                    const cards = [
                      ...section.cards,
                      {
                        id: `card-${crypto.randomUUID()}`,
                        title: `Card ${section.cards.length + 1}`,
                        body: "Replace with approved content.",
                      },
                    ];
                    const sections = [...content.addedSections];
                    sections[sectionIndex] = { ...section, cards };
                    setSections(sections);
                  }}
                >
                  Add card
                </button>
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

export function HomepageVisualEditor({
  draft,
  disabled = false,
  canCreateSections = false,
  canManageSections = false,
  canManageTheme = false,
  onChange,
}: HomepageVisualEditorProps) {
  const [previewDevice, setPreviewDevice] = useState<
    "desktop" | "tablet" | "mobile"
  >("desktop");
  return (
    <div className="homepage-section-editor" data-homepage-schema="v2.1">
      <nav
        aria-label="Homepage sections"
        className="homepage-section-editor__nav"
      >
        <p>Live V2.1 sections</p>
        <ol>
          {homepageEditorSectionDefinitions.map((section, index) => (
            <li key={section.id}>
              <a href={`#homepage-section-${section.id}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {section.label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="homepage-section-editor__workspace">
        <div className="homepage-section-editor__sections">
          {homepageEditorSectionDefinitions.map((section, index) => (
            <details
              className="homepage-section-editor__section"
              id={`homepage-section-${section.id}`}
              key={section.id}
              open={index === 0}
            >
              <summary>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <b>{section.label}</b>
                  <small>{section.description}</small>
                </div>
              </summary>
              <div className="homepage-section-editor__fields">
                {section.id === "theme" ? (
                  <ThemeControls
                    content={draft}
                    disabled={!canManageTheme}
                    onChange={onChange}
                  />
                ) : section.id === "addedSections" ? (
                  <AddedSectionsEditor
                    content={draft}
                    disabled={disabled}
                    canCreate={canCreateSections}
                    canManage={canManageSections}
                    onChange={onChange}
                  />
                ) : (
                  fields[section.id].map((field) => (
                    <EditableField
                      content={draft}
                      disabled={disabled}
                      field={field}
                      key={field.path}
                      onChange={onChange}
                    />
                  ))
                )}
                {section.id === "services" || section.id === "packages" ? (
                  <p className="homepage-section-editor__catalog-note">
                    Cards remain connected to the {section.label} catalog. This
                    editor changes the introduction without duplicating price,
                    duration, availability, or booking data.
                  </p>
                ) : null}
              </div>
            </details>
          ))}
        </div>

        <aside className="homepage-section-editor__preview">
          <header>
            <p className="portal-kicker">Live page preview</p>
            <h2>Wellness Hub V2.1</h2>
            <p>
              Buttons are inactive here. Draft changes are not public until
              published.
            </p>
            <div
              className="homepage-section-editor__device-picker"
              role="group"
              aria-label="Preview device"
            >
              {(["desktop", "tablet", "mobile"] as const).map((device) => (
                <button
                  type="button"
                  data-preview-device={device}
                  aria-pressed={previewDevice === device}
                  key={device}
                  onClick={() => setPreviewDevice(device)}
                >
                  {device}
                </button>
              ))}
            </div>
          </header>
          <div
            className={`homepage-section-editor__preview-frame homepage-section-editor__preview-frame--${previewDevice}`}
          >
            <HomepageContentView
              actions="inactive"
              content={draft}
              semanticContext="embedded"
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
